import React, { FC, useState } from 'react';
import styled from '@emotion/styled';

import { PopoutMenu, PopoutMenuItem } from '~Scenes/Handy/Components/PopoutMenu';
import { Button } from '~Styles';
import MoreHorizIcon from '~/Assets/icons/more_horiz.svg';
import { useButton, useI18n, useLicenses, useLoyalty, useSelector, useStaffCan } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import Icon from '~ComponentsImproved/Icon';
import PersonIcon from '~Assets/icons/person.svg';
import EditIcon from '~/Assets/icons/create.svg';
import PersonEditIcon from '~Assets/icons/person-edit.svg';
import SwapIcon from '~/Assets/icons/swap.svg';
import { useTableUniverse } from '~Components/TableUniverse';
import useModal from '~ComponentsImproved/Modal/useModal';
import LinkUser from '~Components/LinkUser';
import ChangeChequeOwner from '~Scenes/Tables/Components/Footer/Components/ChangeChequeOwner';
import RenameCheque from '~Scenes/Tables/Components/Footer/Components/RenameCheque';
import LoyaltyModal, { ContactlessIcon } from '~Components/LoyaltyModal';

const TriggerButton = styled(Button)`
  z-index: 3;
  width: 64px;
`;

const EditChequeActions: FC = () => {
  const { i18n } = useI18n();
  const { selectedTable } = useTableContext();
  const { selectedCheque } = useChequeContext();
  const universe = useTableUniverse();
  const licenses = useLicenses();
  const linkCustomerModal = useModal();
  const changeOwnerModal = useModal();
  const renameChequeModal = useModal();
  const staffCan = useStaffCan();
  const [storage] = useStorage();
  const terminalId = storage.adyenTerminals[0]?.POIID;

  const loyalty = useLoyalty();

  const deviceWakeState = useSelector((state) => state.deviceWakeState);
  const country = useSelector((state) => state.country);

  const [actionMenuOpen, setActionMenuOpen] = useState<boolean>(false);

  // The first cheque on the table is used as a fallback when we do not have a selected cheque
  const firstChequeOnTable = universe.cheques.find(
    (cheque) => cheque.tableId === selectedTable?.id,
  );

  // `selectedCheque` from ChequeContext is a snapshot taken when it was picked, so it
  // does not reflect later table events (e.g. loyalty user/discount changes). Re-resolve
  // it against the live universe so those updates are picked up.
  const liveSelectedCheque = selectedCheque
    ? universe.cheques.find((cheque) => cheque.id === selectedCheque.id)
    : undefined;

  const chequeToEdit = liveSelectedCheque ?? selectedCheque ?? firstChequeOnTable;

  const staffCanMutateGivenCheque = staffCan.mutateCheque(chequeToEdit?.id || '');

  const triggerButton = useButton({
    onClick: () => setActionMenuOpen((prev) => !prev),
  });

  const memberButton = useButton({
    onClick: () => {
      setActionMenuOpen(false);
      linkCustomerModal.show();
    },
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  const loyaltyButton = useButton({
    onClick: () => {
      setActionMenuOpen(false);
      loyalty.loyaltyModal.show();
    },
    disabled: deviceWakeState.syncingTableEvents || loyalty.isWaitingForCard,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  const changeOwnerButton = useButton({
    onClick: () => {
      setActionMenuOpen(false);
      changeOwnerModal.show();
    },
    disabled: deviceWakeState.syncingTableEvents || !staffCanMutateGivenCheque,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : undefined,
  });

  const renameButton = useButton({
    onClick: () => {
      setActionMenuOpen(false);
      renameChequeModal.show();
    },
    disabled: deviceWakeState.syncingTableEvents || !staffCanMutateGivenCheque,
    disabledToast: deviceWakeState.syncingTableEvents
      ? i18n('Handy.Tables.LoadingEvents')
      : undefined,
  });

  if (!selectedTable) {
    console.warn('EditChequeActions may only be rendered when there is a selected table.');
    return null;
  }

  if (!chequeToEdit) {
    // This can happen if there are no cheques on the table
    return null;
  }

  if (!staffCanMutateGivenCheque && !licenses.hasApp) {
    // If the staff cannot mutate the cheque and we don't have the app license, there's nothing to show here
    return null;
  }

  return (
    <>
      <div id="actions-button" style={{ position: 'relative' }}>
        <TriggerButton padding="none" background="colorBlueGray50" {...triggerButton}>
          <Icon src={MoreHorizIcon} color="var(--colorBlueGray1100)" />
        </TriggerButton>

        <PopoutMenu
          position={selectedCheque ? 'right' : 'left'}
          open={actionMenuOpen}
          rootId="actions-button"
          closeOnClickOutside
          onClose={() => setActionMenuOpen(false)}>
          {country === 'ES' && (
            <PopoutMenuItem {...memberButton}>
              <Icon src={chequeToEdit?.userId ? PersonEditIcon : PersonIcon} />
              {i18n('CustomerForCheckout.Member')}
            </PopoutMenuItem>
          )}

          {licenses.hasLoyaltyPro && (
            <PopoutMenuItem {...loyaltyButton}>
              {loyalty.isWaitingForCard ? (
                <ContactlessIcon size={20} animated />
              ) : (
                <Icon src={chequeToEdit?.userId ? PersonEditIcon : PersonIcon} />
              )}
              {i18n('Loyalty.Title')}
            </PopoutMenuItem>
          )}

          <PopoutMenuItem {...changeOwnerButton}>
            <Icon src={SwapIcon} />
            {i18n('TM.ChangeOwner')}
          </PopoutMenuItem>

          <PopoutMenuItem {...renameButton}>
            <Icon src={EditIcon} />
            {i18n('TM.Rename')}
          </PopoutMenuItem>
        </PopoutMenu>
      </div>

      <LinkUser
        isShowing={linkCustomerModal.isShowing}
        onClose={linkCustomerModal.hide}
        selectedCheque={chequeToEdit.id}
        selectedChequeUserId={chequeToEdit?.userId || null}
      />

      <LoyaltyModal
        isShowing={loyalty.loyaltyModal.isShowing}
        hide={loyalty.loyaltyModal.hide}
        terminalId={terminalId}
        pendingCard={loyalty.pendingCard}
        pendingError={loyalty.pendingError}
        onRequestTapCard={() => loyalty.waitForCard(terminalId)}
        onClearPending={loyalty.clearPending}
        isTsr
        selectedCheque={chequeToEdit.id}
        selectedChequeUserId={chequeToEdit?.userId || null}
      />

      <ChangeChequeOwner
        isShowing={changeOwnerModal.isShowing}
        onClose={changeOwnerModal.hide}
        selectedCheque={chequeToEdit.id}
      />

      <RenameCheque
        isShowing={renameChequeModal.isShowing}
        onClose={renameChequeModal.hide}
        selectedCheque={chequeToEdit.id}
      />
    </>
  );
};

export default EditChequeActions;
